import { Loader2, UserCircle2, XIcon } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Chip, ChipRow, StickyActionBar } from '@/components/mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import CustomerSearch from '@/components/utils/DragAndDropUploader/CustomerSearch';
import { MultiUserSelector } from '@/components/runner/utils/MultiUserSelector';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { cn } from '@/lib/utils';
import type { RootState } from '@/reducers';
import type { ReminderCustomer } from '@/slices/remindersApi';
import {
	type Enrollment,
	type ManualEnrollmentSource,
	useEnrollCustomerMutation,
	useGetEnrollmentsQuery,
	useGetReminderSequencesQuery,
	usePreviewReminderSequenceQuery,
	useUndoEnrollmentMutation,
} from '@/slices/reminderSequencesApi';
import { ActionIcon, formatStepDue, planLength } from './sequenceActions';

const MAX_ASSIGNEES = 20;
const DATE = 'YYYY-MM-DD';
const LAST_SEQUENCE_KEY = 'reminderSequenceLastUsed';

type StartChoice = 'today' | 'tomorrow' | 'pick';

interface AddToReminderSequenceProps {
	/** Fixed customer (conversation, pipeline); otherwise the panel asks */
	customer?: ReminderCustomer | null;
	initialSequenceId?: string;
	pipelineItemId?: string;
	source: ManualEnrollmentSource;
	stickyActions?: boolean;
	onAdded?: (enrollment: Enrollment) => void;
	onCancel?: () => void;
}

const readLastSequence = () => {
	try {
		return localStorage.getItem(LAST_SEQUENCE_KEY) || undefined;
	} catch {
		return undefined;
	}
};

const rememberSequence = (id: string) => {
	try {
		localStorage.setItem(LAST_SEQUENCE_KEY, id);
	} catch {
		// A blocked store just means no remembered choice
	}
};

/**
 * Pick a sequence, a customer and a start day, see every step's date, add.
 * One panel for every entry point: Reminders, the conversation sidebar,
 * pipeline cards and the chat composer.
 */
const AddToReminderSequence = ({
	customer: fixedCustomer,
	initialSequenceId,
	pipelineItemId,
	source,
	stickyActions = false,
	onAdded,
	onCancel,
}: AddToReminderSequenceProps) => {
	const navigate = useNavigate();
	const maskPhone = usePhoneMask();
	const currentUserId = useSelector((state: RootState) => state.auth?.user?._id) as string | undefined;
	const { data: sequencesData, isLoading: loadingSequences } = useGetReminderSequencesQuery();
	const sequences = sequencesData?.sequences ?? [];

	const [sequenceId, setSequenceId] = useState<string | undefined>(initialSequenceId);
	const [customer, setCustomer] = useState<ReminderCustomer | null>(fixedCustomer ?? null);
	const [startChoice, setStartChoice] = useState<StartChoice>('today');
	const [pickedDate, setPickedDate] = useState('');
	const [assignees, setAssignees] = useState<string[] | null>(null);

	const [enroll, { isLoading: adding }] = useEnrollCustomerMutation();
	const [undo] = useUndoEnrollmentMutation();

	// Default to the remembered sequence, else the first one.
	useEffect(() => {
		if (sequenceId || !sequences.length) return;
		const remembered = readLastSequence();
		setSequenceId(sequences.some((s) => s._id === remembered) ? remembered : sequences[0]._id);
	}, [sequences, sequenceId]);

	const sequence = sequences.find((s) => s._id === sequenceId);

	// The sequence's default assignees until the person changes them.
	const defaultAssignees = useMemo(() => {
		if (!sequence) return currentUserId ? [currentUserId] : [];
		if (sequence.assignTo === 'specific' && sequence._assignees?.length) return sequence._assignees;
		return currentUserId ? [currentUserId] : [];
	}, [sequence, currentUserId]);
	const chosenAssignees = assignees ?? defaultAssignees;

	const { data: firstPreview } = usePreviewReminderSequenceQuery(
		{ sequenceId: sequenceId as string },
		{ skip: !sequenceId },
	);
	// "Today" is the business's today, which the server tells us.
	const today = firstPreview?.today ?? moment().format(DATE);
	const startDate =
		startChoice === 'today'
			? today
			: startChoice === 'tomorrow'
				? moment(today, DATE).add(1, 'day').format(DATE)
				: pickedDate || today;

	const { data: preview, isFetching: previewing } = usePreviewReminderSequenceQuery(
		{ sequenceId: sequenceId as string, startDate },
		{ skip: !sequenceId },
	);

	const { data: customerRuns } = useGetEnrollmentsQuery(
		{ _customer: customer?._id, status: 'active' },
		{ skip: !customer?._id },
	);
	const alreadyOn = customerRuns?.enrollments.find((run) => run._reminderSequence === sequenceId);

	const canAdd = Boolean(sequence && customer?._id && !alreadyOn && !adding);

	const handleAdd = async () => {
		if (!sequence || !customer?._id) return;
		try {
			const { enrollment } = await enroll({
				sequenceId: sequence._id,
				_customer: customer._id,
				startDate: startChoice === 'today' ? undefined : startDate,
				_assignees: assignees ?? undefined,
				_pipelineItem: pipelineItemId,
				source,
			}).unwrap();
			rememberSequence(sequence._id);
			const name = customer.name ? maskPhone(customer.name) : 'Customer';
			const steps = enrollment.stepCount;
			toast.success(`${name} added to ${sequence.name}`, {
				description: `${steps} reminder${steps === 1 ? '' : 's'} created`,
				duration: 5000,
				action: {
					label: 'Undo',
					onClick: () => {
						undo(enrollment._id)
							.unwrap()
							.then(() => toast(`${name} removed from ${sequence.name}`))
							.catch(() => toast.error('Too late to undo. Remove them from the sequence instead.'));
					},
				},
			});
			onAdded?.(enrollment);
		} catch (error) {
			const status = (error as { status?: number })?.status;
			toast.error(
				status === 409
					? 'This customer is already on this sequence.'
					: ((error as { data?: { message?: string } })?.data?.message ??
							'Could not add the customer to the sequence.'),
			);
		}
	};

	if (!loadingSequences && sequences.length === 0) {
		return (
			<div className="space-y-4 py-2 text-center">
				<div className="space-y-1.5">
					<p className="text-[15px] font-semibold">No reminder sequences yet</p>
					<p className="mx-auto max-w-sm text-sm text-muted-foreground">
						Build a follow-up plan once, like &ldquo;call on day 1, email on day 2, text on day 7&rdquo;,
						then add customers to it. Each step lands on your Today list the day it&rsquo;s due.
					</p>
				</div>
				<Button onClick={() => navigate('/reminders/sequences?new=1')}>Create a sequence</Button>
			</div>
		);
	}

	const actions = (
		<Button className={cn(stickyActions && 'flex-1')} size={stickyActions ? 'default' : 'sm'} onClick={handleAdd} disabled={!canAdd}>
			{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to sequence'}
		</Button>
	);

	return (
		<div className={cn('space-y-4', stickyActions && 'flex min-h-0 flex-1 flex-col space-y-0')}>
			<div className={cn('space-y-4', stickyActions && 'min-h-0 flex-1 overflow-y-auto px-4 py-4')}>
				<p className="text-sm text-muted-foreground">
					Add a customer to a follow-up plan. Each step becomes a reminder on its day.
				</p>

				<div className="space-y-1.5">
					<Label htmlFor="sequence-picker">Sequence</Label>
					<Select value={sequenceId} onValueChange={(value) => { setSequenceId(value); setAssignees(null); }}>
						<SelectTrigger id="sequence-picker" className="bg-background max-md:h-11 max-md:text-base">
							<SelectValue placeholder={loadingSequences ? 'Loading…' : 'Choose a sequence'} />
						</SelectTrigger>
						<SelectContent>
							{sequences.map((item) => (
								<SelectItem key={item._id} value={item._id}>
									<span className="flex items-center gap-2">
										<span className="truncate">{item.name}</span>
										<span className="text-xs text-muted-foreground">{planLength(item.steps)}</span>
									</span>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="space-y-1.5">
					<Label>Customer</Label>
					{customer ? (
						<div className="flex items-center justify-between rounded-md border border-border bg-background p-2.5 text-sm">
							<div className="flex min-w-0 items-center gap-2">
								<UserCircle2 className="h-5 w-5 shrink-0 text-muted-foreground" />
								<p className="truncate font-medium">{maskPhone(customer.name || 'Customer')}</p>
							</div>
							{!fixedCustomer && (
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setCustomer(null)}
									className="h-7 w-7 text-muted-foreground hover:text-foreground max-md:size-11"
									aria-label="Clear customer"
								>
									<XIcon className="h-4 w-4" />
								</Button>
							)}
						</div>
					) : (
						<CustomerSearch handleSelectCustomer={(c: ReminderCustomer) => setCustomer(c)} />
					)}
					{alreadyOn && (
						<p className="text-[13px] text-muted-foreground" role="status">
							{maskPhone(customer?.name || 'This customer')} is already on this sequence
							{alreadyOn.currentStepNumber
								? ` (step ${alreadyOn.currentStepNumber} of ${alreadyOn.stepCount})`
								: ''}
							.
						</p>
					)}
				</div>

				<div className="space-y-1.5">
					<Label>Starts</Label>
					<ChipRow aria-label="Start day" className="px-0 pb-0 pt-0">
						<Chip active={startChoice === 'today'} onClick={() => setStartChoice('today')}>
							Today
						</Chip>
						<Chip active={startChoice === 'tomorrow'} onClick={() => setStartChoice('tomorrow')}>
							Tomorrow
						</Chip>
						<Chip active={startChoice === 'pick'} onClick={() => setStartChoice('pick')}>
							Pick a date
						</Chip>
					</ChipRow>
					{startChoice === 'pick' && (
						<Input
							type="date"
							aria-label="Start date"
							value={pickedDate}
							min={today}
							onChange={(e) => setPickedDate(e.target.value)}
							className="h-10 w-full bg-background max-md:h-11 sm:w-48"
						/>
					)}
					<p className="text-[13px] text-muted-foreground">Day 1 is the day the customer starts.</p>
				</div>

				<div className="space-y-1.5">
					<Label>Assign to</Label>
					<MultiUserSelector
						title="Unassigned"
						sheetTitle="Assign to"
						max={MAX_ASSIGNEES}
						_selectedUsers={chosenAssignees}
						onSelectUser={(user) => {
							const current = chosenAssignees;
							setAssignees(
								current.includes(user._id)
									? current.filter((id) => id !== user._id)
									: [...current, user._id],
							);
						}}
						className="w-full bg-background font-normal max-md:min-h-11 max-md:text-base touch:min-h-11"
						placeholderClassName="text-sm text-muted-foreground max-md:text-base"
					/>
				</div>

				{sequence && (
					<div className="space-y-1.5">
						<div className="flex items-center gap-2">
							<Label>Plan</Label>
							{previewing && <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-label="Updating dates" />}
						</div>
						<ol className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/70 bg-background">
							{(preview?.steps ?? []).map((step) => {
								const due = moment(step.dueAt);
								const isNow = step.immediate || due.isBefore(moment());
								return (
									<li key={step.stepId} className="flex items-center gap-3 px-3 py-2.5 text-sm">
										<ActionIcon action={step.action} size="sm" />
										<span className="min-w-0 flex-1 truncate">{step.title}</span>
										<span className="shrink-0 text-[13px] text-muted-foreground">
											{isNow ? 'Due now' : formatStepDue(step.dueAt)}
											{step.moved && ' · moved to an open day'}
										</span>
									</li>
								);
							})}
						</ol>
					</div>
				)}
			</div>

			{stickyActions ? (
				<StickyActionBar className="pb-[max(0.5rem,var(--safe-bottom))] kb-open:pb-2">{actions}</StickyActionBar>
			) : (
				<div className="flex justify-end gap-2">
					{onCancel && (
						<Button variant="ghost" size="sm" onClick={onCancel} disabled={adding}>
							Cancel
						</Button>
					)}
					{actions}
				</div>
			)}
		</div>
	);
};

export default AddToReminderSequence;
